import {
  Button,
  Box,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Input,
  InputGroup,
  InputLeftElement,
  useDisclosure,
  Tag,
  Flex,
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
} from "@chakra-ui/react";
import { AiOutlineSearch } from "react-icons/ai";
import { BiFilter } from "react-icons/bi";
import AdminDataTable from "components/AdminDataTable/AdminDataTable";
import Paginator from "components/Paginator";
import AddComposerModal from "components/AddComposerModal";
import { ImCheckmark } from "react-icons/im";
import { GiCancel } from "react-icons/gi";
import { useEffect, useState } from "react";
import { useQueryWrapper } from "services/apiHelper";
import { scriptsRequest } from "services/request";
import { QUERY_KEYS, type ScriptType, statusOBJ } from "utils/constants";
import dayjs from "dayjs";
import { PROTECTED_PATHS } from "routes/pagePath";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaEye, FaPencilAlt } from "react-icons/fa";
import VerifyModal from "components/VerificationPage/VerifyModal";
import { getPublicationIssues } from "components/VerificationPage/PublicationReadiness";
import EditRequestsTable from "components/VerificationPage/EditRequestsTable";
import EditRequestDiffModal from "components/VerificationPage/EditRequestDiffModal";
import { CgAsterisk } from "react-icons/cg";
import { successToast, errorToast } from "utils/hooks";

type FilterType = "All" | "Pending" | "Published" | "Cancelled";

const getCategoryLabels = (script: any) => {
  const linkedCategories = (script.categories ?? [])
    .map((category) => category?.name)
    .filter((name): name is string => Boolean(name));
  if (linkedCategories.length > 0) return linkedCategories;

  if (script.category === "others" && script.customCategory) return [script.customCategory];
  if (typeof script.category !== "string") return [];

  return [script.category].filter(Boolean);
};

const CategoryCell = ({ script }: { script: any }) => {
  const categories = getCategoryLabels(script);
  if (categories.length === 0) return <Text color="#7f94a6">-</Text>;

  const visibleCategories = categories.slice(0, 2);
  const hiddenCount = categories.length - visibleCategories.length;

  return (
    <Flex alignItems="center" gap="1" wrap="wrap">
      <Text>{visibleCategories.join(", ")}</Text>
      {hiddenCount > 0 && (
        <Tooltip label={categories.join(", ")} hasArrow>
          <Tag size="sm" colorScheme="blue">
            +{hiddenCount}
          </Tag>
        </Tooltip>
      )}
    </Flex>
  );
};

const getLanguageLabels = (script: any) => {
  const linkedLanguages = (script.languages ?? [])
    .map((language) => language?.name)
    .filter((name): name is string => Boolean(name));
  if (linkedLanguages.length > 0) return linkedLanguages;

  if (typeof script.language !== "string") return [];

  return script.language
    .split(",")
    .map((language) => language.trim())
    .filter(Boolean);
};

const LanguageCell = ({ script }: { script: any }) => {
  const languages = getLanguageLabels(script);
  if (languages.length === 0) return <Text color="#7f94a6">-</Text>;

  const visibleLanguages = languages.slice(0, 2);
  const hiddenCount = languages.length - visibleLanguages.length;

  return (
    <Flex alignItems="center" gap="1" wrap="wrap">
      <Text>{visibleLanguages.join(", ")}</Text>
      {hiddenCount > 0 && (
        <Tooltip label={languages.join(", ")} hasArrow>
          <Tag size="sm" colorScheme="blue">
            +{hiddenCount}
          </Tag>
        </Tooltip>
      )}
    </Flex>
  );
};

const VerificationPage = () => {
  const { APPROVED, PENDING, REJECTED } = statusOBJ;
  const history = useNavigate();
  const [currentScript, setCurrentScript] = useState<any>();
  const [isApproved, setIsApproved] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchParams, setSearchParam] = useSearchParams();
  const {
    isOpen: isOpenAddComposer,
    onOpen: onOpenAddComposer,
    onClose: onCloseAddComposer,
  } = useDisclosure();
  const [newComposer, setNewComposer] = useState("");
  const [selectedEditRequestId, setSelectedEditRequestId] = useState<string | null>(null);
  const [selectedEditRequestScript, setSelectedEditRequestScript] = useState<ScriptType | null>(
    null,
  );
  const {
    isOpen: isOpenEditRequest,
    onOpen: onOpenEditRequest,
    onClose: onCloseEditRequest,
  } = useDisclosure();

  const currentTab = searchParams.get("tab") === "edit-requests" ? 1 : 0;
  const handleTabChange = (index: number) => {
    const newParams = new URLSearchParams(searchParams);
    if (index === 1) {
      newParams.set("tab", "edit-requests");
    } else {
      newParams.delete("tab");
    }
    setSearchParam(newParams);
  };

  const COLUMNS = [
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Composer",
      accessor: "composer",
      Cell: function uploaded({ cell }: any) {
        const composer = cell.row.original.scriptComposer?.name;
        const suggestedComposer = cell.row.original.composer;
        return (
          <>
            {composer ? (
              <div>{composer}</div>
            ) : (
              <Flex>
                {suggestedComposer}
                <div style={{ color: "red" }}>
                  <CgAsterisk />
                </div>
                <Button
                  onClick={() => {
                    setNewComposer(suggestedComposer);
                    void copyToClipboard(suggestedComposer);
                    onOpenAddComposer();
                  }}
                >
                  Add
                </Button>
              </Flex>
            )}
          </>
        );
      },
    },
    {
      Header: "Uploaded By",
      accessor: "user.userName",
    },
    {
      Header: "Published",
      Cell: function verified({ cell }: any) {
        const status = cell.row.original.isVerified;
        const hasEditRequest = Boolean(cell.row.original.hasEditRequest);
        const editRequestId = cell.row.original.editRequestId;
        return (
          <Flex direction="column" gap={1} align="flex-start">
            <Tag
              borderRadius="full"
              bg={status === APPROVED ? "#BBE5B3" : status === REJECTED ? "orangered" : "#FFEA8A"}
              color="#414F3E"
              fontSize="xs"
              textTransform="capitalize"
            >
              {status === APPROVED ? APPROVED : status === REJECTED ? REJECTED : PENDING}
            </Tag>
            {hasEditRequest && (
              <Tag
                borderRadius="full"
                colorScheme="orange"
                size="sm"
                fontSize="2xs"
                cursor="pointer"
                title="View edit request in Edit Requests tab"
                onClick={(e) => {
                  e.stopPropagation();
                  if (editRequestId) {
                    setSelectedEditRequestId(editRequestId);
                    setSelectedEditRequestScript(cell.row.original);
                    onOpenEditRequest();
                    return;
                  }
                  handleTabChange(1);
                }}
              >
                Edit Pending
              </Tag>
            )}
          </Flex>
        );
      },
    },
    {
      Header: "Uploaded On",
      Cell: function uploaded({ cell }: any) {
        return <div> {dayjs(cell.row.original.createdAt).format("MMM D YYYY")}</div>;
      },
    },

    {
      Header: "Category",
      Cell: function category({ cell }: any) {
        return <CategoryCell script={cell.row.original} />;
      },
    },
    {
      Header: "Language",
      Cell: function language({ cell }: any) {
        return <LanguageCell script={cell.row.original} />;
      },
    },
    {
      Header: "Publication readiness",
      Cell: ({ cell }) => {
        if (cell.row.original.isVerified !== PENDING) return <Text>-</Text>;
        const issues = getPublicationIssues(cell.row.original);
        return (
          <Text fontSize="sm" color={issues.length ? "orange.700" : "green.700"}>
            {issues.length ? issues.join(" ") : "Open review to check publication details"}
          </Text>
        );
      },
    },
    {
      Header: "Edit",
      Cell: function edit({ cell }) {
        return (
          <Button
            onClick={() => {
              history(
                `${PROTECTED_PATHS.NEW_EDIT_SCRIPT}?edit=${encodeURIComponent(
                  cell.row.original.slug || cell.row.original.id,
                )}`,
              );
            }}
          >
            <FaPencilAlt />
          </Button>
        );
      },
    },

    {
      Header: "Approve",
      Cell: ({ cell }) => (
        <Button
          isDisabled={cell.row.original.isVerified !== PENDING}
          bg="green"
          onClick={() => {
            setCurrentScript(cell.row.original);
            setIsApproved(true);
            onOpen();
          }}
        >
          <ImCheckmark />
        </Button>
      ),
    },
    {
      Header: "View",
      Cell: ({ cell }: any) => (
        <Button
          variant="secondary"
          onClick={() => {
            const origin = window.location.origin;
            const page = `/scripts/${cell.row.original.slug}`;
            const url = origin + page;
            window.open(url, "_blank");
          }}
        >
          <FaEye />
        </Button>
      ),
    },
    {
      Header: "Reject",
      Cell: ({ cell }: any) => (
        <Button
          isDisabled={cell.row.original.isVerified !== "Pending"}
          bg="orangered"
          onClick={() => {
            setCurrentScript(cell.row.original);
            setIsApproved(false);
            onOpen();
          }}
        >
          <GiCancel />
        </Button>
      ),
    },
  ];
  const [allScripts, setAllScripts] = useState<any>([]);
  const onSuccess = (res) => {
    setAllScripts(res.data);
  };
  const { isLoading, refetch: refetchAdminScripts } = useQueryWrapper(
    QUERY_KEYS.ADMIN_SCRIPTS,
    scriptsRequest.SCRIPTS_ADMIN,
    {
      onSuccess,
    },
  );

  const initalFilter = searchParams.get("curFilter") ?? "Pending";
  const initalPage = Number(searchParams.get("pageNo")) ?? 0;
  const initalSort = searchParams.get("sort") ?? "name";

  const [filterOpt, setFilterOpt] = useState<FilterType>(initalFilter as FilterType);
  const [filteredData, setfilteredData] = useState<ScriptType[]>([]);
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState(initalSort);

  const [currentPage, setCurrentPage] = useState(initalPage || 0);
  const [postsPerPage, setPostsPerPage] = useState(10);

  const lastPostIndex = (currentPage + 1) * postsPerPage;
  const firstPostIndex = lastPostIndex - postsPerPage;
  const currentScripts = filteredData.slice(firstPostIndex, lastPostIndex);
  const pageCountCal = Math.ceil(filteredData.length / postsPerPage);
  const pageCount = pageCountCal < 1 ? 1 : pageCountCal;
  useEffect(() => {
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      setfilteredData(
        allScripts.filter(({ name, composer }) => {
          const scriptName = name?.toLowerCase() ?? "";
          const composerName = composer?.toLowerCase() ?? "";
          return scriptName.includes(searchLower) || composerName.includes(searchLower);
        }),
      );
    } else {
      setfilteredData(allScripts);
    }
  }, [allScripts, searchText]);

  useEffect(() => {
    let currentFiltered = allScripts;
    switch (filterOpt) {
      case "All":
        // setfilteredData(allScripts);
        currentFiltered = allScripts;
        break;
      case "Published":
        // setfilteredData(allScripts.filter(({ isVerified }) => isVerified === "Published"));
        currentFiltered = allScripts.filter(({ isVerified }) => isVerified === "Published");

        break;
      case "Cancelled":
        // setfilteredData(allScripts.filter(({ isVerified }) => isVerified === "Cancelled"));
        currentFiltered = allScripts.filter(({ isVerified }) => isVerified === "Cancelled");
        break;
      default:
        // setfilteredData(allScripts.filter(({ isVerified }) => isVerified === "Pending"));
        currentFiltered = allScripts.filter(({ isVerified }) => isVerified === "Pending");

        break;
    }
    const sorted = currentFiltered.sort(sorter(sortBy));
    setfilteredData(sorted);
  }, [filterOpt, allScripts]);

  const sortTable = (selected) => {
    setSortBy(selected);
    const sorted = filteredData.sort(sorter(selected));
    setfilteredData(sorted);
    setSearchParam({ curFilter: filterOpt, pageNo: currentPage.toString(), sort: selected });
  };
  const pendingEditCount = allScripts.filter((s: any) => Boolean(s?.hasEditRequest)).length;

  return (
    <>
      <Box
        mt="4"
        ml={{
          base: "0",
          md: "0",
          lg: "0",
          xl: "0",
        }}
      >
        <Text
          as="h1"
          fontSize="1.3rem"
          color="#4e6e97"
          mt="4"
          ml="4"
          mb="4"
          textTransform="uppercase"
        >
          Script verification
        </Text>

        <Tabs index={currentTab} onChange={handleTabChange} colorScheme="blue" isLazy>
          <TabList px="4" borderBottomWidth="2px">
            <Tab fontWeight="semibold" fontSize="md">
              Script Submissions
            </Tab>
            <Tab fontWeight="semibold" fontSize="md">
              Edit Requests
              {pendingEditCount > 0 && (
                <Badge ml={2} colorScheme="red" borderRadius="full" px={2} fontSize="xs">
                  {pendingEditCount}
                </Badge>
              )}
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel p={0} pt={3}>
              <Box p={"0 10px"} mt="2">
                <InputGroup mb={[8, 8, null]} w="100%">
                  <InputLeftElement pointerEvents="none">
                    <AiOutlineSearch color="gray.300" />
                  </InputLeftElement>
                  <Input
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                    }}
                    type="search"
                    placeholder="Search your scripts"
                  />
                </InputGroup>
              </Box>
              <Flex>
                <Box textAlign="right">
                  <Menu>
                    <MenuButton
                      mx={2}
                      as={Button}
                      aria-label="Sort scripts"
                      leftIcon={<BiFilter />}
                      variant="outline"
                      fontWeight="normal"
                    >
                      Sort: {sortBy}
                    </MenuButton>
                    <MenuList minWidth="240px" zIndex={14}>
                      <MenuOptionGroup
                        onChange={sortTable}
                        defaultValue="name"
                        title="Sort"
                        type="radio"
                      >
                        <MenuItemOption value="name">Name</MenuItemOption>
                        <MenuItemOption value="composer">Composer</MenuItemOption>
                        <MenuItemOption value="category">Category</MenuItemOption>
                        <MenuItemOption value="user">Uploaded by</MenuItemOption>
                        <MenuItemOption value="createdAt">Uploaded On</MenuItemOption>
                      </MenuOptionGroup>
                    </MenuList>
                  </Menu>
                </Box>

                <Box textAlign="right">
                  <Menu>
                    <MenuButton
                      mx={2}
                      as={Button}
                      aria-label="Filter scripts"
                      leftIcon={<BiFilter />}
                      variant="outline"
                      fontWeight="normal"
                    >
                      Filter: {filterOpt}
                    </MenuButton>
                    <MenuList minWidth="240px" zIndex={14}>
                      <MenuOptionGroup
                        onChange={(selected: any) => {
                          setFilterOpt(selected);
                          setCurrentPage(0);
                          setSearchParam({ curFilter: selected, pageNo: "0", sort: sortBy });
                        }}
                        defaultValue="all"
                        title="Filter"
                        type="radio"
                      >
                        <MenuItemOption value="All">All</MenuItemOption>
                        <MenuItemOption value="Pending">Pending</MenuItemOption>
                        <MenuItemOption value="Published">Published</MenuItemOption>
                        <MenuItemOption value="Cancelled">Cancelled</MenuItemOption>
                      </MenuOptionGroup>
                    </MenuList>
                  </Menu>
                </Box>
              </Flex>
              <Box p="4">
                <AdminDataTable
                  ariaLabel="Admin scripts"
                  columns={COLUMNS}
                  data={currentScripts}
                  loading={isLoading}
                  showRowNumbers
                  startIndex={+postsPerPage * currentPage + 1}
                />
              </Box>
              <>
                <Paginator
                  pageCount={pageCount}
                  onPageChange={(page) => {
                    setSearchParam({
                      curFilter: filterOpt,
                      pageNo: page.selected.toString(),
                      sort: sortBy,
                    });
                    setCurrentPage(page.selected);
                  }}
                  pageNo={currentPage}
                  pageSize={postsPerPage}
                  onSizeChange={(selectedPage) => {
                    setPostsPerPage(+selectedPage);
                    setCurrentPage(0);
                    setSearchParam({ curFilter: filterOpt, pageNo: "0", sort: sortBy });
                  }}
                />
              </>
            </TabPanel>

            <TabPanel p={0} pt={3}>
              <EditRequestsTable adminScripts={allScripts} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
      <VerifyModal
        isOpen={isOpen}
        onClose={onClose}
        isApproved={isApproved}
        script={currentScript}
      />
      <AddComposerModal
        isOpen={isOpenAddComposer}
        onClose={onCloseAddComposer}
        newComposer={newComposer}
      />
      <EditRequestDiffModal
        isOpen={isOpenEditRequest}
        onClose={() => {
          setSelectedEditRequestId(null);
          setSelectedEditRequestScript(null);
          onCloseEditRequest();
        }}
        requestId={selectedEditRequestId}
        scriptFallback={selectedEditRequestScript}
        onSuccess={() => {
          void refetchAdminScripts();
        }}
      />
    </>
  );
};

export default VerificationPage;

const sorter = (key) => {
  if (key === "composer") {
    return function (a, b) {
      const first = a?.scriptComposer?.name;
      const second = b?.scriptComposer?.name;
      if (first === null && second === null) {
        return 0;
      }
      if (first === null) {
        return 1;
      }
      if (second === null) {
        return -1;
      }
      if (first < second) {
        return -1;
      }
      if (first > second) {
        return 1;
      }
      return 0;
    };
  }

  if (key === "user") {
    return function (a, b) {
      const first = a?.user?.userName;
      const second = b?.user?.userName;
      if (first < second) {
        return -1;
      }
      if (first > second) {
        return 1;
      }
      return 0;
    };
  }
  return function (a, b) {
    const isCreatedAt = key === "createdAt";
    const first = a[key] || "";
    const second = b[key] || "";

    if (first < second) {
      return isCreatedAt ? 1 : -1;
    }
    if (first > second) {
      return isCreatedAt ? -1 : 1;
    }
    return 0;
  };
};

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    successToast("Copied!!");
  } catch (err) {
    errorToast("Failed to copy text: ");
  }
}
